/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import * as React from 'react';
import { useCallback } from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import { useCanvas, useStory } from '../../../../app';
import useFocusCanvas from '../../../canvas/useFocusCanvas';
import { nestedOffsetCalcFunc } from './constants';
import { ReorderableGroupLayer } from './reorderableGroupLayer';
import { ReorderableElementLayer } from './reorderableElementLayer';
import useReorderable from '../../../reorderable/useReorderable';

export function ReorderableLayer({ isGroup, ...rest }: { isGroup: boolean }) {
  const { setSelectedElementsById, selectedElementIds, elements } = useStory(
    ({ actions, state }) => ({
      setSelectedElementsById: actions.setSelectedElementsById,
      selectedElementIds: state.selectedElementIds,
      elements: state.currentPage?.elements || [],
    })
  );
  const focusCanvas = useFocusCanvas();
  const renamableLayer = useCanvas(({ state }) => state.renamableLayer);

  const handleStartReordering = useCallback(
    (item) => () => {
      if (isGroup) {
        const groupElementIds = elements
          .filter((element) => element.groupId === item.id)
          .map((element) => element.id);

        if (groupElementIds.length > 0 && selectedElementIds.length <= 1) {
          setSelectedElementsById({
            elementIds: groupElementIds,
            withLinked: true,
          });
        }
      } else {
        const isMultiSelection =
          selectedElementIds.length > 1 &&
          selectedElementIds.includes(item.id);

        if (!isMultiSelection) {
          setSelectedElementsById({ elementIds: [item.id] });
        }
      }

      if (renamableLayer) {
        focusCanvas();
      }
    },
    [
      isGroup,
      elements,
      selectedElementIds,
      setSelectedElementsById,
      focusCanvas,
      renamableLayer,
    ]
  );

  const {
    state: { reorderingGroupId },
  } = useReorderable();

  const props = {
    ...rest,
    reorderingGroupId,
    handleStartReordering,
    nestedOffsetCalcFunc,
  };

  if (isGroup) {
    return <ReorderableGroupLayer {...props} />;
  }

  return <ReorderableElementLayer {...props} />;
}
